import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import fallbackImage from "../../Assets/fallbackimg_giftcard.png";
import copyIcon from "../../Assets/thank-you-order-details/copy.svg";
import brandHeaderChevron from "../../Assets/thank-you-order-details/chevron.svg";
import failedBrandHeaderChevron from "../../Assets/thank-you-order-details/chevron-muted.svg";
import summaryChevronIcon from "../../Assets/thank-you-order-details/summary-chevron.svg";
import statusCardSuccessIcon from "../../Assets/thank-you-order-details/status-card-success.svg";
import statusCardArrowIcon from "../../Assets/thank-you-order-details/status-card-arrow.svg";
import statusCardFailedIcon from "../../Assets/thank-you-order-details/status-card-failed.svg";
import statusCardFailedArrowIcon from "../../Assets/thank-you-order-details/status-card-failed-arrow.svg";
import orderFailedAlertIcon from "../../Assets/thank-you-order-details/order-failed-alert.svg";
import failedRefundSeparator from "../../Assets/thank-you-order-details/failed-refund-separator.svg";
import { getImageUrl } from "../Utility/Utility";

const money = (value, currencySymbol, decimals = 0) => {
  const numericValue = Number(value || 0);
  const fractionDigits = decimals > 0 && !Number.isInteger(numericValue)
    ? decimals
    : 0;

  return `${currencySymbol}${numericValue.toLocaleString("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

export default function ThankYouOrderDetails({
  orderDetails,
  productData,
  productMapping,
  translation,
  issuedVouchers = [],
  gcIdConfig = "",
  voucherLoading = false,
  informationSection,
  orderFailure = false,
  orderResponse,
  showRedeemPointData = true,
}) {
  const [isExpanded, setIsExpanded] = useState(() => window.innerWidth > 700);
  const [copiedKey, setCopiedKey] = useState("");
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(
    () => window.innerWidth <= 700,
  );

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 700px)");
    const syncResponsiveState = ({ matches }) => {
      setIsExpanded(!matches);
      setShowFeeBreakdown(matches);
    };

    mobileQuery.addEventListener("change", syncResponsiveState);
    return () => mobileQuery.removeEventListener("change", syncResponsiveState);
  }, []);
  const translate = (...keys) =>
    keys.map((key) => translation?.[key]).find((value) => value?.trim()) || "";
  const currencySymbol = translate("rupee_Symbol");
  const formatMoney = (value, decimals = 0) =>
    money(value, currencySymbol, decimals);
  const orderItems = useMemo(
    () => (orderDetails?.orderItems || []).filter((item) => Number(item?.quantity) > 0),
    [orderDetails?.orderItems],
  );

  const totals = useMemo(() => {
    const denomination = orderItems.reduce(
      (sum, item) => sum + Number(item?.denomination || 0) * Number(item?.quantity || 0),
      0,
    );
    const quantity = orderItems.reduce(
      (sum, item) => sum + Number(item?.quantity || 0),
      0,
    );
    const discountPercent = Number(productMapping?.customerDiscount || 0);
    const discount = (denomination * discountPercent) / 100;
    const feePercent = Number(productMapping?.convenienceFee || 0);
    const gstPercent = Number(productMapping?.gst || 0);
    const baseAfterDiscount = denomination - discount;
    const fee = (baseAfterDiscount * feePercent) / 100;
    const gst = (fee * gstPercent) / 100;
    const total = Number(orderDetails?.totalPayableAmount) || baseAfterDiscount + fee + gst;

    return { denomination, quantity, discount, fee, gst, total, feePercent };
  }, [orderDetails?.totalPayableAmount, orderItems, productMapping]);
  const totalFee = totals.fee + totals.gst;
  const hasConvenienceFee = totalFee > 0;
  const feeAmount = formatMoney(totalFee, hasConvenienceFee ? 2 : 0);

  const product = productData?.data || {};
  const productName =
    orderDetails?.cardName ||
    orderDetails?.name ||
    product?.name ||
    translate("eGiftCard");
  const productImage = getImageUrl(orderDetails?.cardImage || product?.small) || fallbackImage;
  const paidWithPoints =
    showRedeemPointData &&
    Number(orderDetails?.burnPoints) > 0 &&
    Number(orderDetails?.totalPayableAmount) <= 0;
  const orderAmount = paidWithPoints
    ? `${Math.round(Number(orderDetails?.burnPoints)).toLocaleString("en-IN")} ${translate("pts")}`
    : formatMoney(totals.total, 2);
  const displayedOrderId = orderResponse?.orderId || orderDetails?.orderId || "—";
  const statusTitle = orderFailure
    ? translate("thankYouOrderFailed")
    : translate("thankYouTitle");
  const isSingleVoucher = totals.quantity === 1;
  const purchaseSuccessMessage = isSingleVoucher
    ? translate("voucherPurchasedSuccessfully")
    : translate("vouchersPurchasedSuccessfully");
  const statusMessage = orderFailure
    ? translate("gift_voucher_purchase_failed")
    : `${totals.quantity} ${purchaseSuccessMessage}`;
  const failureMessage = translate("thankYouOrderCancelledNote");
  const failedVoucherLabel = `${totals.quantity} ${
    isSingleVoucher
      ? translate("voucherFailed")
      : translate("vouchersFailed")
  }`;

  const defaultVoucherIdLabel = translate("voucherId");
  const amountLabel = translate("amount");
  const voucherCodeLabel = issuedVouchers?.[0]?.labels?.cardNumber;
  const pinLabel = issuedVouchers?.[0]?.labels?.cardPin;
  const expiryLabel = translate("expires");
  const productVoucherCountLabel = isSingleVoucher
    ? translate("thankYouVoucherDelivered")
    : translate("thankYouVouchersDelivered");
  const summaryVoucherCountLabel = isSingleVoucher
    ? translate("thankYouGiftVoucher")
    : translate("thankYouGiftVouchers");
  const getVoucherIdentifier = (voucher) => {
    const hasValue = (value) =>
      value !== null && value !== undefined && String(value).trim() !== "";
    const normalizeKey = (key) =>
      String(key || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
    const findValueByKey = (source, keys) => {
      const normalizedKeys = new Set(keys.map(normalizeKey));
      return Object.entries(source || {}).find(
        ([key, value]) => normalizedKeys.has(normalizeKey(key)) && hasValue(value),
      )?.[1];
    };
    const configuredGcIdField = String(gcIdConfig || "").trim();
    const configuredGcId =
      configuredGcIdField && configuredGcIdField !== "-"
        ? voucher?.[configuredGcIdField]
        : undefined;
    const legacyGcId = findValueByKey(voucher, [
        "gcid",
        "gc_id",
        "giftCardId",
        "gift_card_id",
        "giftCardIdentifier",
      ]);
    const gcId = configuredGcIdField ? configuredGcId : legacyGcId;

    if (hasValue(gcId)) {
      return {
        label: defaultVoucherIdLabel,
        value: gcId,
      };
    }

    const voucherId = findValueByKey(voucher, ["voucherId", "voucher_id", "cardId"]);

    return {
      label: defaultVoucherIdLabel,
      value: hasValue(voucherId) ? voucherId : "—",
    };
  };
  const voucherIdLabel = getVoucherIdentifier(issuedVouchers?.[0]).label;

  const copyValue = async (value, key) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey(""), 1800);
  };

  return (
    <main
      className={`thank-you-order-details thank-you-order-confirmation${
        orderFailure ? " thank-you-order-details--failed" : ""
      }`}
    >
      <div className="thank-you-order-details__layout">
        <aside className="thank-you-order-details__sidebar">
          <section className="thank-you-status-card">
            <div
              className={`thank-you-status-card__body${
                orderFailure ? " thank-you-status-card__body--failed" : ""
              }`}
            >
              <div className="thank-you-status-card__headline">
                {orderFailure ? (
                  <img
                    className="thank-you-status-card__status-icon"
                    src={statusCardFailedIcon}
                    alt=""
                  />
                ) : (
                  <img
                    className="thank-you-status-card__status-icon"
                    src={statusCardSuccessIcon}
                    alt=""
                  />
                )}
                <div>
                  <h1>{statusTitle}</h1>
                  <p>{statusMessage}</p>
                </div>
              </div>

              <div className="thank-you-status-card__order">
                <div>
                  <strong>{displayedOrderId}</strong>
                  <span>{translate("thankYouOrderNumber")}</span>
                </div>
                <div className="thank-you-status-card__amount">
                  <strong>{orderAmount}</strong>
                  <span>{translate("thankYouOrderAmount")}</span>
                </div>
              </div>
            </div>

            <Link
              to={orderFailure ? "/contact-us" : "/"}
              className={`thank-you-status-card__shop${
                orderFailure ? " thank-you-status-card__shop--failed" : ""
              }`}
            >
              <span>
                {orderFailure
                  ? translate("thankYouContactSupport")
                  : translate("thankYouKeepShopping", "keepShoppingSaveMore", "keepShoppingEarnMore")}
              </span>
              <img
                src={orderFailure ? statusCardFailedArrowIcon : statusCardArrowIcon}
                alt=""
              />
            </Link>
          </section>

          <div className="thank-you-delivery-note">
            {orderFailure
              ? failureMessage
              : translate("thankYouDetailsSent")}
          </div>

          <div className="thank-you-order-summary-section">
            <h2>{translate("thankYouOrderSummary")}</h2>
            <section className="thank-you-order-summary">
              <div className="thank-you-order-summary__row">
                <span>
                  {totals.quantity}{" "}
                  {summaryVoucherCountLabel}
                </span>
                <strong>{formatMoney(totals.denomination)}</strong>
              </div>
              <div className="thank-you-order-summary__row">
                <span>{translate("thankYouTotalDiscount")}</span>
                <strong>{formatMoney(totals.discount, 2)}</strong>
              </div>
              {/* No convenience fee on the order: hide the fee row and its breakdown */}
              {hasConvenienceFee && (
                <>
                  <div className="thank-you-order-summary__row thank-you-order-summary__fee-title">
                    <button
                      type="button"
                      aria-expanded={orderFailure || showFeeBreakdown}
                      onClick={() => {
                        if (!orderFailure) setShowFeeBreakdown((value) => !value);
                      }}
                    >
                      <span>{translate("thankYouConvenienceFee")}</span>
                      <img src={summaryChevronIcon} alt="" />
                    </button>
                    <strong>{feeAmount}</strong>
                  </div>
                  {(orderFailure || showFeeBreakdown) && (
                    <div className="thank-you-order-summary__row thank-you-order-summary__muted">
                      <span>
                        {productName} · {totals.feePercent}% {translate("thankYouPlusGst")}
                      </span>
                      <span>{feeAmount}</span>
                    </div>
                  )}
                </>
              )}
              <div className="thank-you-order-summary__row thank-you-order-summary__total">
                <span>{translate("thankYouTotalAmount")}</span>
                <strong>{orderAmount}</strong>
              </div>
              {orderFailure && (
                <div className="thank-you-order-summary__row thank-you-order-summary__refund">
                  <span>{translate("thankYouRefundInitiated")}</span>
                  <strong>{orderAmount}</strong>
                </div>
              )}
            </section>
          </div>
        </aside>

        <section
          className={`thank-you-voucher thank-you-confirmation-product${
            isExpanded ? " is-expanded" : ""
          }${orderFailure ? " thank-you-voucher--failed" : ""}`}
        >
          {orderFailure ? (
            <button
              type="button"
              className="thank-you-voucher__header thank-you-voucher__header--failed"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((value) => !value)}
            >
              <img
                className="thank-you-voucher__logo"
                src={productImage}
                alt={productName}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = fallbackImage;
                }}
              />
              <span className="thank-you-voucher__identity">
                <strong>{productName}</strong>
                <span className="thank-you-voucher__metadata thank-you-voucher__metadata--failed">
                  <span>{failedVoucherLabel}</span>
                  <img src={failedRefundSeparator} alt="" />
                  <span>{translate("refundInitiated")}</span>
                </span>
              </span>
              <strong className="thank-you-voucher__value">
                {orderAmount}
              </strong>
              <img
                className="thank-you-voucher__chevron thank-you-voucher__chevron--failed"
                src={failedBrandHeaderChevron}
                alt=""
              />
            </button>
          ) : (
            <button
              type="button"
              className="thank-you-voucher__header"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((value) => !value)}
            >
              <img
                className="thank-you-voucher__logo"
                src={productImage}
                alt={productName}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = fallbackImage;
                }}
              />
              <span className="thank-you-voucher__identity">
                <strong>{productName}</strong>
                <span className="thank-you-voucher__metadata">
                  <span>
                    {totals.quantity}{" "}
                    {productVoucherCountLabel}
                  </span>
                </span>
              </span>
              <strong className="thank-you-voucher__value">
                {orderAmount}
              </strong>
              <img
                className="thank-you-voucher__chevron"
                src={brandHeaderChevron}
                alt=""
              />
            </button>
          )}

          {orderFailure && isExpanded && (
            <div className="thank-you-voucher__failure">
              <div className="thank-you-voucher__failure-alert">
                <img src={orderFailedAlertIcon} alt="" />
                <div className="thank-you-voucher__failure-copy">
                  <strong>
                    {productName} {translate("couldNotBeIssued")}
                  </strong>
                  <span>
                    {orderAmount}{" "}
                    {translate("refundInitiatedMessage")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {!orderFailure && isExpanded && (
            <div className="thank-you-voucher__content">
              {!voucherLoading && issuedVouchers.length > 0 && (
                <div className="thank-you-voucher-cards">
                  {issuedVouchers.map((voucher, index) => {
                    const voucherIdentifier = getVoucherIdentifier(voucher);
                    const voucherId = voucherIdentifier.value;
                    const amount =
                      voucher?.Amount ??
                      orderItems[index]?.denomination ??
                      orderItems[0]?.denomination;
                    const voucherCode = String(
                      voucher?.CardNumber || voucher?.VoucherCode || "",
                    );
                    const displayedVoucherCode = /^\d{16}$/.test(voucherCode)
                      ? voucherCode.replace(
                          /^(\d{5})(\d{6})(\d{5})$/,
                          "$1 $2 $3",
                        )
                      : voucherCode;
                    const pin = String(voucher?.CardPin || "");
                    const expiry = voucher?.Validity
                      ? new Date(voucher.Validity)
                      : null;
                    const expiryDate =
                      expiry && !Number.isNaN(expiry.getTime())
                        ? expiry
                            .toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                        : "—";

                    return (
                      <article
                        className="thank-you-voucher-card"
                        key={`${voucherId}-mobile-${index}`}
                      >
                        <div className="thank-you-voucher-card__top">
                          <div className="thank-you-voucher-card__identity">
                            <span>
                              {voucherIdentifier.label}: {voucherId}
                            </span>
                            <span>
                              {expiryLabel}: {expiryDate}
                            </span>
                          </div>
                          <strong>{formatMoney(amount)}</strong>
                        </div>
                        <div className="thank-you-voucher-card__details">
                          <div>
                            <span>{voucher?.labels?.cardPin}</span>
                            <strong>{pin || "—"}</strong>
                          </div>
                          <div>
                            <span>{voucher?.labels?.cardNumber}</span>
                            <strong className="thank-you-voucher-card__code">
                              {displayedVoucherCode || "—"}
                              {voucherCode && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    copyValue(voucherCode, `mobile-code-${index}`)
                                  }
                                  aria-label={translate("copyVoucherCode")}
                                >
                                  <img src={copyIcon} alt="" />
                                </button>
                              )}
                              {copiedKey === `mobile-code-${index}` && (
                                <small>
                                  {translate("copied", "copiedSuccessfully")}
                                </small>
                              )}
                            </strong>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
              <div
                className="thank-you-confirmation-table"
                role="table"
                aria-label={translate("issuedVouchers")}
              >
                <div className="thank-you-confirmation-table__row thank-you-confirmation-table__head" role="row">
                  <span>{voucherIdLabel}</span>
                  <span>{amountLabel}</span>
                  <span>{voucherCodeLabel}</span>
                  <span>{pinLabel}</span>
                  <span>{expiryLabel}</span>
                </div>
                {voucherLoading && (
                  <div className="thank-you-confirmation-table__message">
                    {translate("loadingVoucherDetails")}
                  </div>
                )}
                {!voucherLoading && issuedVouchers.map((voucher, index) => {
                  const voucherIdentifier = getVoucherIdentifier(voucher);
                  const voucherId = voucherIdentifier.value;
                  const amount =
                    voucher?.Amount ?? orderItems[index]?.denomination ?? orderItems[0]?.denomination;
                  const voucherCode = String(voucher?.CardNumber || voucher?.VoucherCode || "");
                  const displayedVoucherCode = /^\d{16}$/.test(voucherCode)
                    ? voucherCode.replace(/^(\d{5})(\d{6})(\d{5})$/, "$1 $2 $3")
                    : voucherCode;
                  const pin = String(voucher?.CardPin || "");
                  const expiry = voucher?.Validity ? new Date(voucher.Validity) : null;
                  const expiryDate = expiry && !Number.isNaN(expiry.getTime())
                    ? expiry.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—";

                  return (
                    <div className="thank-you-confirmation-table__row" role="row" key={`${voucherId}-${index}`}>
                      <span data-label={voucherIdentifier.label}>{voucherId}</span>
                      <strong data-label={amountLabel}>{formatMoney(amount)}</strong>
                      <strong data-label={voucher?.labels?.cardNumber} className="thank-you-confirmation-table__copy-value">
                        {displayedVoucherCode || "—"}
                        {voucherCode && (
                          <button
                            type="button"
                            onClick={() => copyValue(voucherCode, `code-${index}`)}
                            aria-label={translate("copyVoucherCode")}
                          >
                            <img src={copyIcon} alt="" />
                          </button>
                        )}
                        {copiedKey === `code-${index}` && <small>{translate("copied", "copiedSuccessfully")}</small>}
                      </strong>
                      <strong data-label={voucher?.labels?.cardPin} className="thank-you-confirmation-table__copy-value">
                        {pin || "—"}
                        {pin && (
                          <button
                            type="button"
                            onClick={() => copyValue(pin, `pin-${index}`)}
                            aria-label={translate("copyPin")}
                          >
                            <img src={copyIcon} alt="" />
                          </button>
                        )}
                        {copiedKey === `pin-${index}` && <small>{translate("copied", "copiedSuccessfully")}</small>}
                      </strong>
                      <span data-label={expiryLabel} className="thank-you-confirmation-table__expiry">{expiryDate}</span>
                    </div>
                  );
                })}
                {!voucherLoading && issuedVouchers.length === 0 && (
                  <div className="thank-you-confirmation-table__message">
                    {translate("voucherDetailsUnavailable", "NoRecordsFound")}
                  </div>
                )}
              </div>

              <div className="thank-you-voucher__information">
                {informationSection}
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
